import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField,  Container, Alert } from "@mui/material";
import { getcampaigns } from "../../../services/api";
import { fetchCustomers, addCustomer, updateCustomer } from "../../../services/customerModule/customers/customerServices.js";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { AUTO_COMPLETE, VARIANTS,  SORT_ORDERS, SIZES, STATUS } from "../../../common/constants/commonConstants.js";
import { CUSTOMER_STRINGS, CUSTOMER_KEYS, CUSTOMER_ERRORS, CUSTOMER_MESSAGES } from "../../../common/constants/customerModule/customerConstants.js";
import CustomerDashTable from "./CustomerDashTable.jsx";
import { CUSTOMER_DASH_TABLE_COLUMNS } from "../../../common/constants/dashboardModule/customerDashConstants..js";
import CustomerDashInfo from "./CustomerDashInfo.jsx";

const CustomersDash = () => {
    const [page, setPage] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchCustomer, setSearchCustomer] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(CUSTOMER_KEYS.NAME);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [viewCustomerGuid, setViewCustomerGuid] = useState(null);

    useEffect(() => { 
        loadCustomers();
        loadCampaigns(); 
    }, []);

    const loadCustomers = async () => {
        try {
            setLoadingCustomers(true);
            setCustomers(await fetchCustomers());
        }
        catch (error) {
            console.error(CUSTOMER_ERRORS.FETCH_CUSTOMERS_ERROR, error);
        }
        finally {
            setLoadingCustomers(false);
        }
    };

    const loadCampaigns = async () => {
        try {
            setLoadingCampaigns(true);
            let data = await getcampaigns();
            data = data.sort((a, b) => a.id - b.id);
            setCampaigns(data);
        }
        catch (error) {
            console.error("Error fetching campaigns:", error);
        }
        finally {
            setLoadingCampaigns(false);
        }
    };

    const customersList = customers.map((customer) => ({
        ...customer,
        name: `${customer.firstname ?? ""} ${customer.lastname ?? ""}`.trim(),
    }));

    const { customersToDisplay, filteredCustomersLength } = useMemo(() => {
        const search = searchCustomer.toLowerCase().trim();

        const filteredCustomers = customersList.filter((customer) => {
            const customerId = customer.customerid?.toString().toLowerCase() || "";
            const firstName = customer.firstname?.toLowerCase() || "";
            const lastName = customer.lastname?.toLowerCase() || "";
            const fullName = customer.name?.toLowerCase() || "";

            return (
                customerId.includes(search) ||
                firstName.includes(search) ||
                lastName.includes(search) ||
                fullName.includes(search)
            );
        });

        return {
            customersToDisplay: filteredCustomers
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredCustomersLength: filteredCustomers.length,
        };
    }, [customersList, searchCustomer, order, orderBy, page, rowsPerPage]);

    const handleAddEditCustomerClick = (customer = {}) => {
        setSelectedCustomer(customer);
        // setIsAddEditCustomerModalOpen(true);
    };

    // const handleSaveCustomer = async () => {
    //     if (!selectedCustomer) return;
    //     try {
    //         if (selectedCustomer.id) {
    //             await updateCustomer(selectedCustomer.id, selectedCustomer);
    //             setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_UPDATED);
    //         } else {
    //             await addCustomer(selectedCustomer);
    //             setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_ADDED);
    //         }
    //         setIsAddEditCustomerModalOpen(false);
    //         await loadCustomers();
    //         setTimeout(() => setSuccessMessage(""), 3000);
    //     } catch (error) {
    //         console.error(CUSTOMER_ERRORS.SAVE_CUSTOMER_ERROR, error);
    //     }
    // };

    const handleInputChange = (field) => (e) =>
        setSelectedCustomer({ ...selectedCustomer, [field]: e.target.value });

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    const handleViewCustomer = (customer) => {
        setViewCustomerGuid(customer.customerguid);
    };

    if (viewCustomerGuid) {
        return <CustomerDashInfo customerguid={viewCustomerGuid} onBack={() => setViewCustomerGuid(null)} />;
    }

    return (
        <>
            <Container width="xl" disableGutters>
                {successMessage && (
                    <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                        {successMessage}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={SIZES.GROW}>
                        <TextField
                            fullWidth
                            size={SIZES.SMALL}
                            value={searchCustomer}
                            variant={VARIANTS.OUTLINED}
                            autoComplete={AUTO_COMPLETE.OFF}
                            label={CUSTOMER_STRINGS.SEARCH_CUSTOMERS}
                            onChange={e => setSearchCustomer(e.target.value)}
                        />
                    </Grid>
                   
                </Grid>
                <CustomerDashTable
                    page={page}
                    order={order}
                    setPage={setPage}
                    orderBy={orderBy}
                    campaigns={campaigns}
                    rowsPerPage={rowsPerPage}
                    customers={customersToDisplay}
                    setRowsPerPage={setRowsPerPage}
                    columns={CUSTOMER_DASH_TABLE_COLUMNS}
                    onEdit={handleAddEditCustomerClick}
                    onView={handleViewCustomer}
                    handleRequestSort={handleRequestSort}
                    isCustomersLoading={loadingCustomers}
                    isCampaignsLoading={loadingCampaigns}
                    filteredCustomersLength={filteredCustomersLength}
                />
              
            </Container>
        </>
    )
};

export default CustomersDash;